import "./SignupPage.scss";

import { Link, useNavigate } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import { z } from 'zod';
import { useForm } from '../../hooks/Form';
import { useCurrentUser } from '../../UserProvider';
import { api, setAuthToken } from '../../utils/api';
import { useEffect } from "react";
import SsoButtons from "../LoginPage/SsoButtons";
import usePageTitle from "../../hooks/PageTitle";

const schema = z.object({
  email: z.string()
    .nonempty("Email is required")
    .email("Invalid email address"),
  password: z.string()
    .nonempty("Password is required")
    .min(8, "Password must be at least 8 characters")
    .regex(/\d/, "Password must contain at least one number")
    .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
  passwordConfirmation: z.string().nonempty("Password confirmation is required")
}).refine((data) => data.password === data.passwordConfirmation, {
  message: "Passwords do not match",
  path: ["passwordConfirmation"],
});

function createInitialValues() {
  return {
    email: '',
    password: '',
    passwordConfirmation: ''
  };
}

function createInitialErrors() {
  return {
    email: null,
    password: null,
    passwordConfirmation: null
  };
}

export default function SignupPage() {
  const navigate = useNavigate();
  const { refreshUser, currentUser } = useCurrentUser();

  usePageTitle("Signup");

  const { isLoading, setIsLoading, error, setError, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  useEffect(() => {
    if (currentUser) {
      navigate('/home', { replace: true });
    }
  }, [currentUser, navigate]);

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors(createInitialErrors());

      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      const { data } = await api.post(`/users`, {
        email: result.data.email,
        password: result.data.password
      });

      setAuthToken(data);

      await refreshUser();

      navigate('/chat');

    } catch {
      setError("Signup failed. Please try again.");

    } finally {
      setIsLoading(false);
    }
  };

  /*useEffect(() => {
    document.title = 'Signup - Papaya';
  }, []);
  */
  return (
    <Fragment>
      <form onSubmit={handleSubmit} className='form'>
        <h1 className='form__title'>
          Let's get this show <br /> on the road!
        </h1>
        <p className='form__subtitle'>
          Create an account to start <br /> applying for gigs
        </p>
        <SsoButtons />
        <div className='form__field-group'>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='email' className='text-field__label'>
              E-mail
            </label>
            <input
              id='email'
              name='email'
              type="text"
              placeholder="name@email.com"
              value={values.email}
              required
              onChange={(e) => setFieldValue('email', e.target.value)}
              className='text-field__input'
            />
            {errors.email && (
              <p className='text-field__error'>
                {errors.email}
              </p>
            )}
          </div>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='password' className='text-field__label'>
              Password
            </label>
            <input
              id='password'
              name='password'
              type="password"
              placeholder="• • • • • • • •"
              value={values.password}
              required
              onChange={(e) => setFieldValue('password', e.target.value)}
              className='text-field__input'
            />
            {errors.password && (
              <p className='text-field__error'>
                {errors.password}
              </p>
            )}
          </div>
          <div className='text-field text-field--white form__field'>
            <label htmlFor='password_confirmation' className='text-field__label'>
              Confirm Password
            </label>
            <input
              id='password_confirmation'
              name='password_confirmation'
              type="password"
              placeholder="• • • • • • • •"
              value={values.passwordConfirmation}
              required
              onChange={(e) => setFieldValue('passwordConfirmation', e.target.value)}
              className='text-field__input'
            />
            {errors.passwordConfirmation && (
              <p className='text-field__error'>
                {errors.passwordConfirmation}
              </p>
            )}
          </div>
        </div>
        {error && (
          <p className='error error--black'>
            {error}
          </p>
        )}
        <div className='form__button-group'>
          <Link className='button button--white button--outline' to='/login'>
            Already have an account
          </Link>
          <button type='submit' className='button button--white button--fill'>
            {isLoading ? "Loading..." : "Create your account!"}
          </button>
        </div>
      </form>
    </Fragment>
  );
}
